import React, { useState } from "react";
import { Link as RouterLink, useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { deviceDetect } from "react-device-detect";
// import { DeviceUUID } from "device-uuid";
import { v4 as uuidv4 } from "uuid";
// import { publicIp } from 'public-ip';
import { logInSuccess } from "../../redux/slices/userSlice";
import {
  Box,
  Button,
  Flex,
  Checkbox,
  IconButton,
  Input,
  InputGroup,
  FormControl,
  InputRightElement,
  FormLabel,
  Link,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Text,
  VStack,
  FormErrorMessage,
  useDisclosure,
  useColorModeValue,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import api from "api/axiosConfig";
import API_BASE from "configs/hostConfig";
import BackgroundWrapper from "components/Background/Background";

const Signin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [showPassword, setShowPassword] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();

  // const deviceUuid = new DeviceUUID().get();
  const deviceUuid = uuidv4();
  const deviceInfo = deviceDetect();
  const deviceType = 0;
  let deviceName;
  if (deviceInfo.isBrowser) {
    deviceName = deviceInfo.browserName;
  } else if (deviceInfo.isMobile || deviceInfo.isTablet) {
    deviceName = deviceInfo.model;
  }

  const validationSchema = Yup.object({
    email: Yup.string()
      .required("Email or username is required")
      .min(4, "Email or username must be at least 4 characters long"),
    password: Yup.string()
      .required("Password is required")
      .min(6, "Password must be at least 6 characters long"),
  });

  function getReturnUrl() {
    const searchParams = new URLSearchParams(location.search);
    let returnUrl = searchParams.get("returnUrl");
    if (!returnUrl) {
      returnUrl = "/student-profile/assignment";
    }
    return returnUrl;
  }

  function handleLogInSuccess() {
    navigate(getReturnUrl());
    // console.log('user log in data: ', userData);
    // todo: fetch data related to current user
  }

  const handleSubmit = async (values, actions) => {
    // console.log('Submitted values:', values);
    // const loginIp = (await publicIp.v4()) || (await publicIp.v6());
    // const loginAddress = loginIp;

    const body = {
      username: values.email,
      password: values.password,
      rememberMe: values.rememberMe,
      deviceUuid,
      deviceType,
      deviceName,
      // loginIp,
      // loginAddress,
    };
    api
      .post(`${API_BASE}/api/auth/sign-in`, body)
      .then(({ data }) => {
        // console.log('auth/sign-in response data: ', data);
        dispatch(logInSuccess(data));
        // handleLogInSuccess(data);
        handleLogInSuccess();
        // navigate('/student-profile/assignment');
      })
      .catch((err) => console.log("failed to sign in reason: ", err))
      .finally(() => actions.setSubmitting(false));
  };

  const sendResetPasswordEmail = (values) => {
    // console.log('sendResetPasswordEmail values:', values);
    api
      .post(`${API_BASE}/api/auth/send-reset-password-email`, values)
      .then((res) => {
        if (res.status == 200) {
          onClose();
          navigate("/signin");
        }
      })
      .catch(() =>
        console.log(
          "failed to sendResetPasswordEmail with email: ",
          values.email
        )
      );
  };

  return (
    <BackgroundWrapper applyMd={true}>
      <Box
        bg="white"
        p={8}
        boxShadow="lg"
        borderRadius="lg"
        width="400px"
        maxWidth="90%"
        mx="auto"
        my={12}
      >
        <Flex justifyContent="space-between" mb={6}>
          <Text fontSize="xl" fontWeight="bold">
            Sign In
          </Text>

          <Link as={RouterLink} to="/signup" mt={2} color="teal.500">
            I don’t have an account
          </Link>
        </Flex>

        <Formik
          initialValues={{ email: "", password: "", rememberMe: true }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form>
              <VStack spacing={4}>
                <Field name="email">
                  {({ field, form }) => (
                    <FormControl
                      isInvalid={form.errors.email && form.touched.email}
                    >
                      <FormLabel>Email or Username</FormLabel>
                      <Input
                        {...field}
                        type="text"
                        placeholder="Enter your email or username"
                      />
                      <FormErrorMessage>{form.errors.email}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>

                <Field name="password">
                  {({ field, form }) => (
                    <FormControl
                      isInvalid={form.errors.password && form.touched.password}
                    >
                      <FormLabel>Password</FormLabel>
                      <InputGroup>
                        <Input
                          {...field}
                          type={showPassword ? "text" : "password"}
                          placeholder="Enter your password"
                        />
                        <InputRightElement>
                          <IconButton
                            icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                            variant="ghost"
                            aria-label="Toggle Password Visibility"
                            onClick={() => setShowPassword(!showPassword)}
                          />
                        </InputRightElement>
                      </InputGroup>
                      <FormErrorMessage>
                        {form.errors.password}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>

                <Button
                  colorScheme="green"
                  width="full"
                  type="submit"
                  isLoading={isSubmitting} // Spinner when submitting
                >
                  Sign In
                </Button>
              </VStack>
              <Flex justifyContent="space-between" mt={4}>
                <Field name="rememberMe">
                  {({ field }) => (
                    <Checkbox {...field} isChecked={field.value}>
                      Remember Me
                    </Checkbox>
                  )}
                </Field>
                <Link color="teal" fontSize="md" onClick={onOpen}>
                  Forget password?
                </Link>
              </Flex>
            </Form>
          )}
        </Formik>

        {/* Forgot Password Modal */}
        <Modal isOpen={isOpen} onClose={onClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Forget your password?</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Formik
                initialValues={{ email: "" }}
                onSubmit={(values) => {
                  sendResetPasswordEmail(values);
                }}
              >
                {({ isSubmitting }) => (
                  <Form>
                    <VStack spacing={4} align="start">
                      <Text
                        fontSize={{ base: "sm", sm: "md" }}
                        color={useColorModeValue("gray.800", "gray.400")}
                      >
                        You&apos;ll get an email with a reset link
                      </Text>
                      <Field name="email">
                        {({ field }) => (
                          <FormControl isRequired>
                            {/* <FormLabel>Email</FormLabel> */}
                            <Input
                              {...field}
                              type="email"
                              placeholder="your-email@domain.com"
                            />
                          </FormControl>
                        )}
                      </Field>

                      {/* <Box fontSize="sm" color="gray.500" mt={2}>
                        Note: This form is only for resetting the primary
                        account password. For secondary accounts, the primary
                        account holder can update the password directly from the
                        profile page.
                      </Box> */}

                      <Button
                        colorScheme="green"
                        width="full"
                        type="submit"
                        isLoading={isSubmitting}
                      >
                        Send Reset Password Email
                      </Button>
                    </VStack>
                  </Form>
                )}
              </Formik>
            </ModalBody>

            <ModalFooter>
              <Button variant="ghost" onClick={onClose}>
                Close
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
      {/* </Flex> */}
    </BackgroundWrapper>
  );
};

export default Signin;
